import { useState, type FunctionComponent } from 'react';
import { type ImageFieldsFragment, type VariationOptionFieldsFragment } from '../../../../__generated__/graphql-client-types';
import { Popover } from '../../../common-components/popover/popover.component';
import { CloudinaryImage } from '../../../images/cloudinary-image/cloudinary-image.component';
import HelpCircleIcon from '../../../svgs/help-circle.svg';
import {
	ProductOptionsDrawer,
	ProductOptionsDropdown,
	ProductOptionsGrid
} from '../components/product-options-group-wrappers/product-options-group-wrappers.component';
import { VariationOptionPill } from '../components/variation-option-pill/variation-option-pill.component';

type ProductOptionGroupHelpProps = {
	title: string;
	helpText?: string;
	helpImage?: ImageFieldsFragment;
};

const ProductOptionGroupHelp: FunctionComponent<ProductOptionGroupHelpProps> = ({ title, helpText, helpImage }) => {
	const [isHelpOpen, setIsHelpOpen] = useState(false);
	const triggerElement = <HelpCircleIcon data-testid="help-icon" className={`pointer f5-ns f6`} />;

	return (
		<Popover toggleElement={triggerElement} isVisible={isHelpOpen} setIsVisible={setIsHelpOpen} direction="bottom">
			<div className={`flex flex-column pa2 f6 w-100`}>
				<strong>{title}</strong>
				{helpText && <p className="ma0 mt1" dangerouslySetInnerHTML={{ __html: helpText }} />}
				{helpImage && (
					<CloudinaryImage
						publicID={helpImage.id}
						description={helpImage.description}
						className="mw-none"
						options={{ crop: 'limit', width: undefined, height: 400 }}
					/>
				)}
			</div>
		</Popover>
	);
};

export type ProductVariationGroupProps = {
	name: string;
	helpText?: string;
	helpImage?: ImageFieldsFragment;
	options: VariationOptionFieldsFragment[];
};

export const ProductVariationGroup: FunctionComponent<ProductVariationGroupProps> = ({ name, helpImage, helpText, options }) => {
	const selectedName = options.find((option) => option.isSelected)?.name || '';
	return (
		<div className="mt1">
			<header className={'flex flex-row flex-nowrap items-center w-100 w-auto-ns mt3'} data-testid="product-group-header">
				<h3 className={`ma0 tc1-title w-90 w-auto-ns mr2 f5-ns f6`}>{name}</h3>
				{(helpText || helpImage) && <ProductOptionGroupHelp title={name} helpText={helpText || ''} helpImage={helpImage} />}
			</header>
			<div className="dn db-ns">
				{options.length > 4 ? (
					<ProductOptionsDropdown selectedName={selectedName}>
						{options.map((option, index) => (
							<VariationOptionPill key={index + option.name} option={option} isDropdown={true} showCheck={true} />
						))}
					</ProductOptionsDropdown>
				) : (
					<ProductOptionsGrid>
						{options.map((option, index) => (
							<VariationOptionPill key={index + option.name} option={option} />
						))}
					</ProductOptionsGrid>
				)}
			</div>
			<div className="db dn-ns">
				<ProductOptionsDrawer groupName={name} selectedName={selectedName}>
					{options.map((option, index) => (
						<div className="mt1" key={index + option.name}>
							<VariationOptionPill option={option} showCheck={true} />
						</div>
					))}
				</ProductOptionsDrawer>
			</div>
		</div>
	);
};
